import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import './SpellsPanel.css';
import SpellService from '../../services/SpellService';
import { SLOT_IDX } from '../../core/Build';
import {
    calcSpellRows,
    calcWeaponCardRow,
    getTypeId,
    statsAtPosition,
    isEliotropeClass,
    isSpellAvailableAtLevel,
} from '../../core/spellUtils';
import SpellCard from './SpellCard';
import {
    spellLineCriterionSignature,
    spellWeightKey,
    WEAPON_WEIGHT_KEY,
} from '../../core/criteriaWeights';
import SpellCardSkeleton from './SpellCardSkeleton';
import WeaponSpellCard from './WeaponSpellCard';
import BestWeaponCard from './BestWeaponCard';
import WeaponTypeFilter from '../utils/WeaponTypeFilter';

const SKELETON_COUNT = 20;

const ELM_LBL = {
    fire: { fr: 'Feu', en: 'Fire', de: 'Feuer', es: 'Fuego', pt: 'Fogo' },
    air: { fr: 'Air', en: 'Air', de: 'Luft', es: 'Aire', pt: 'Ar' },
    water: { fr: 'Eau', en: 'Water', de: 'Wasser', es: 'Agua', pt: 'Água' },
    earth: { fr: 'Terre', en: 'Earth', de: 'Erde', es: 'Tierra', pt: 'Terra' },
    neutral: { fr: 'Neutre', en: 'Neutral', de: 'Neutral', es: 'Neutral', pt: 'Neutro' },
    push: { fr: 'Poussée', en: 'Push', de: 'Schubs', es: 'Empuje', pt: 'Empurrão' },
    heal_total: { fr: 'Soin', en: 'Heal', de: 'Heilung', es: 'Cura', pt: 'Cura' },
    best_element: { fr: 'Meilleur élément', en: 'Best element', de: 'Bestes Element', es: 'Mejor elemento', pt: 'Melhor elemento' },
};

const TXT_LOADING = { fr: 'Chargement des sorts...', en: 'Loading spells...', de: 'Zauber werden geladen...', es: 'Cargando hechizos...', pt: 'Carregando feitiços...' };
const TXT_EMPTY = { fr: 'Aucun sort offensif.', en: 'No damaging spell.', de: 'Kein Schadenszauber.', es: 'Ningún hechizo ofensivo.', pt: 'Nenhum feitiço ofensivo.' };
const TXT_ERR = { fr: 'Erreur de chargement des sorts.', en: 'Failed to load spells.', de: 'Fehler beim Laden der Zauber.', es: 'Error al cargar los hechizos.', pt: 'Erro ao carregar os feitiços.' };
const TXT_RETRY = { fr: 'Réessayer', en: 'Retry', de: 'Erneut versuchen', es: 'Reintentar', pt: 'Tentar novamente' };
const TXT_OTHER = { fr: 'Autres', en: 'Other', de: 'Sonstige', es: 'Otros', pt: 'Outros' };
const TXT_SUMMONS = { fr: 'invocations', en: 'summons', de: 'Beschwörungen', es: 'invocaciones', pt: 'invocações' };
const TXT_DAMAGE_WIP_NOTE = {
    fr: "Cette section est en cours d'amélioration : signalez les sorts problématiques sur le serveur Discord de la communauté.",
    en: 'This section is being improved: report problematic spells on the community Discord server.',
    de: 'Dieser Bereich wird gerade verbessert: Melde problematische Zauber auf dem Discord-Server der Community.',
    es: 'Esta sección está en proceso de mejora: informa de los hechizos problemáticos en el servidor de Discord de la comunidad.',
    pt: 'Esta seção está em processo de melhoria: relate os feitiços problemáticos no servidor Discord da comunidade.',
};

const WEAPON_SLOT = SLOT_IDX.WEAPON;

const normalizeSpellData = (data) => ({
    spells: Array.isArray(data?.spells) ? data.spells : [],
    otherSpells: Array.isArray(data?.other) ? data.other : [],
    summonCatalog: data?.summonCatalog && typeof data.summonCatalog === 'object'
        ? data.summonCatalog
        : {},
});

const weaponSlotLockMatches = (locked, item) => {
    if (!item) return locked === true;
    if (!locked || typeof locked !== 'object') return false;
    return Number(locked.id) === Number(item.id);
};

const SecTitle = ({ title, open, onToggle }) => (
    <button
        type="button"
        className="spells-panel-subtitle-toggle"
        onClick={onToggle}
        aria-expanded={open}
    >
        <h4 className="left spells-panel-subtitle">{title}</h4>
        <span className="spells-panel-subtitle-toggle-icon" aria-hidden="true">{open ? '▾' : '▸'}</span>
    </button>
);

const SpellsPanel = ({
    build,
    setBuild,
    lvl,
    lang = 'fr',
    isGenerating = false,
    genConfig,
    setGenConfig,
    mobileTargetCheckbox = false,
    canEditGenerator = true,
    // The "section being improved" note; hidden with the generator toggled off.
    showNote = true,
}) => {
    const clsName = build?.getCharacterClass?.()?.name || build?.characterClass?.name;
    const typeId = useMemo(() => getTypeId(clsName), [clsName]);
    const cachedSpellData = typeId ? SpellService.getCachedClassSpells(typeId) : null;
    const initialSpellData = normalizeSpellData(cachedSpellData);

    const [spells, setSpells] = useState(() => initialSpellData.spells);
    const [otherSpells, setOtherSpells] = useState(() => initialSpellData.otherSpells);
    const [summonCatalog, setSummonCatalog] = useState(() => initialSpellData.summonCatalog);
    const [loading, setLoading] = useState(() => Boolean(typeId && !cachedSpellData));
    const [err, setErr] = useState(false);
    const [retryNonce, setRetryNonce] = useState(0);
    const [summonsOpen, setSummonsOpen] = useState(true);
    const [otherOpen, setOtherOpen] = useState(false);

    const selectedSpellIdsRef = useRef(new Set());

    useEffect(() => {
        selectedSpellIdsRef.current = new Set([
            ...(build?.maximizeSpellIds || []),
            ...(build?.maximizeSpellCriteria || []).map((c) => c?.spell_id ?? c?.spellId),
        ]);
    }, [build?.maximizeSpellIds, build?.maximizeSpellCriteria]);

    useEffect(() => {
        let alive = true;
        if (!typeId) {
            setSpells([]);
            setOtherSpells([]);
            setSummonCatalog({});
            setOtherOpen(false);
            setErr(false);
            setLoading(false);
            return () => { };
        }

        const applyData = (data) => {
            const next = normalizeSpellData(data);
            const selectedIds = selectedSpellIdsRef.current;
            const hasActiveOtherOnLoad = next.otherSpells.some((sp) => selectedIds.has(sp?.id));
            setSpells(next.spells);
            setOtherSpells(next.otherSpells);
            setOtherOpen(hasActiveOtherOnLoad);
            setSummonCatalog(next.summonCatalog);
        };

        const cached = SpellService.getCachedClassSpells(typeId);
        if (cached && retryNonce === 0) {
            applyData(cached);
            setErr(false);
            setLoading(false);
            return () => {
                alive = false;
            };
        }

        const load = async () => {
            setLoading(true);
            setErr(false);
            try {
                const data = await SpellService.getClassSpells(typeId, { force: retryNonce > 0 });
                if (!alive) return;
                applyData(data);
            } catch (e) {
                if (!alive) return;
                console.error('[Spells] load error', e);
                setErr(true);
                setSpells([]);
                setOtherSpells([]);
                setOtherOpen(false);
                setSummonCatalog({});
            } finally {
                if (alive) setLoading(false);
            }
        };

        load();
        return () => {
            alive = false;
        };
    }, [typeId, retryNonce]);

    const handleRetrySpells = useCallback(() => {
        if (!typeId) return;
        setRetryNonce((n) => n + 1);
    }, [typeId]);

    // The spell cards read the same sheet the stat panel shows, passives and
    // all, so a card never claims damage the panel above it does not account
    // for. `displayPassiveBonuses` governs both: a passive fires at some point
    // in a fight, and the cards are for what the build does reliably.
    const includePassiveEffects = genConfig?.displayPassiveBonuses === true;
    const passiveOverrides = useMemo(() => genConfig?.passiveOverrides || {}, [genConfig?.passiveOverrides]);
    // And at the range the toggle says the character fights at: only that
    // side's % Melee or % Ranged multiplies the hit.
    const position = genConfig?.position;
    const stats = useMemo(
        () => (build ? statsAtPosition(build.getStats({ includePassiveEffects, passiveOverrides }), position) : null),
        [build, includePassiveEffects, passiveOverrides, position],
    );
    const eliotropePortalCardOnly = useMemo(() => isEliotropeClass(clsName), [clsName]);
    const selectedSpellLineSet = useMemo(() => new Set(
        (build?.maximizeSpellCriteria || [])
            .map(spellLineCriterionSignature)
            .filter(Boolean)
    ), [build?.maximizeSpellCriteria]);
    const selectedSpellSet = useMemo(() => {
        const out = new Set(build?.maximizeSpellIds || []);
        (build?.maximizeSpellCriteria || []).forEach((c) => {
            const id = Number(c?.spell_id ?? c?.spellId);
            if (Number.isFinite(id)) out.add(id);
        });
        return out;
    }, [build?.maximizeSpellIds, build?.maximizeSpellCriteria]);
    const availableSpells = useMemo(
        () => (spells || []).filter((sp) => isSpellAvailableAtLevel(sp, lvl)),
        [spells, lvl],
    );
    const availableOtherSpells = useMemo(
        () => (otherSpells || []).filter((sp) => isSpellAvailableAtLevel(sp, lvl)),
        [otherSpells, lvl],
    );
    const spellCharges = build?.spellCharges;
    const rows = useMemo(
        () => calcSpellRows({
            spells: availableSpells, lvl, stats, lang, eliotropePortalCardOnly, summonCatalog, charges: spellCharges,
        }),
        [availableSpells, lvl, stats, lang, eliotropePortalCardOnly, summonCatalog, spellCharges],
    );
    const otherRowsRaw = useMemo(
        () => calcSpellRows({
            spells: availableOtherSpells, lvl, stats, lang, eliotropePortalCardOnly, summonCatalog, charges: spellCharges,
        }),
        [availableOtherSpells, lvl, stats, lang, eliotropePortalCardOnly, summonCatalog, spellCharges],
    );
    const summonRows = useMemo(
        () => rows.filter((sp) => Array.isArray(sp?.summonChildRows) && sp.summonChildRows.length > 0),
        [rows],
    );
    const regularRows = useMemo(
        () => rows.filter((sp) => !Array.isArray(sp?.summonChildRows) || sp.summonChildRows.length === 0),
        [rows],
    );
    const otherRows = useMemo(
        () => otherRowsRaw.filter((sp) => !Array.isArray(sp?.summonChildRows) || sp.summonChildRows.length === 0),
        [otherRowsRaw],
    );
    const hasActiveOtherSpellSelection = useMemo(
        () => otherRowsRaw.some((sp) => selectedSpellSet.has(sp.id)),
        [otherRowsRaw, selectedSpellSet],
    );
    const hasSelectableSpellLines = useCallback((sp) => {
        const ok = (line) => line?.kind === 'damage' || line?.kind === 'heal';
        if ((sp?.baseLines || []).some(ok)) return true;
        if ((sp?.conditionalBlocks || []).some((block) => (block?.lines || []).some(ok))) return true;
        return (sp?.summonChildRows || []).some((row) =>
            (row?.baseLines || []).some(ok)
            || (row?.conditionalBlocks || []).some((block) => (block?.lines || []).some(ok))
        );
    }, []);
    const unselectableSpellIds = useMemo(() => {
        const out = new Set();
        [...rows, ...otherRowsRaw].forEach((sp) => {
            if (!hasSelectableSpellLines(sp)) out.add(sp.id);
        });
        return out;
    }, [rows, otherRowsRaw, hasSelectableSpellLines]);
    const labels = useMemo(
        () => Object.fromEntries(Object.entries(ELM_LBL).map(([key, v]) => [key, v[lang] || v.en])),
        [lang]
    );
    const weaponRow = useMemo(() => calcWeaponCardRow({
        weaponItem: build?.getWeapon?.(),
        stats,
        lvl,
        lang,
        weaponMaging: build?.getWeaponMaging?.() ?? null,
    }), [build, stats, lvl, lang]);
    const weaponSelected = !!build?.maximizeWeapon;
    const bestWeaponSelected = !!build?.maximizeBestWeapon;
    // Not on mobile: the cards are too narrow for a column, and the gen bar's
    // criteria sheet edits the same weights with a row each.
    const showWeights = canEditGenerator && !mobileTargetCheckbox && genConfig?.displayWeights === true;
    // One bundle rather than four props repeated across every card call site.
    // Null when the weights column is off, which is also what hides the cells.
    const weights = useMemo(() => (showWeights ? {
        valueOf: (criterion) => build?.getCriterionWeight?.(spellWeightKey(criterion)) ?? 1,
        onChange: (criterion, next) => {
            const key = spellWeightKey(criterion);
            if (!key || !build || !setBuild) return;
            setBuild(build.setCriterionWeight(key, next));
        },
        disabled: isGenerating,
        lang,
    } : null), [showWeights, build, setBuild, isGenerating, lang]);
    const weaponWeight = build?.getCriterionWeight?.(WEAPON_WEIGHT_KEY) ?? 1;
    const handleWeaponWeightChange = useCallback((next) => {
        if (!build || !setBuild) return;
        setBuild(build.setCriterionWeight(WEAPON_WEIGHT_KEY, next));
    }, [build, setBuild]);
    const spellSelectable = canEditGenerator;
    const spellMobileTargetCheckbox = mobileTargetCheckbox && canEditGenerator;
    // Read-only (compact / community) still highlights the criteria lines; only
    // the toggling is disabled (spellSelectable).
    const visibleSelectedLineKeys = selectedSpellLineSet;

    const handleToggleSpellLine = useCallback(
        (criterion) => {
            if (!build || !setBuild || isGenerating || !canEditGenerator) return;
            const spellId = Number(criterion?.spell_id ?? criterion?.spellId);
            if (!Number.isFinite(spellId) || unselectableSpellIds.has(spellId)) return;
            setBuild(build.toggleMaximizeSpellLine(criterion));
        },
        [build, setBuild, isGenerating, canEditGenerator, unselectableSpellIds]
    );

    // A charged line read at a chosen count: display and score both follow it,
    // whether or not the line is a criterion yet.
    const handleChargesChange = useCallback(
        (spellId, lineKeys, n, max) => {
            if (!build || !setBuild || isGenerating) return;
            setBuild((lineKeys || []).reduce(
                (next, key) => next.setSpellLineCharges(spellId, key, n, max),
                build,
            ));
        },
        [build, setBuild, isGenerating]
    );

    useEffect(() => {
        if (!canEditGenerator || !build || !setBuild) return;
        const current = [
            ...(Array.isArray(build.maximizeSpellIds) ? build.maximizeSpellIds : []),
            ...(Array.isArray(build.maximizeSpellCriteria) ? build.maximizeSpellCriteria : [])
                .map((c) => c?.spell_id ?? c?.spellId),
        ];
        if (!current.length) return;
        const toRemove = current.filter((id) => unselectableSpellIds.has(id));
        if (!toRemove.length) return;
        const nextBuild = build.clearMaximizeSpells();
        if (nextBuild !== build) setBuild(nextBuild);
    }, [build, setBuild, canEditGenerator, unselectableSpellIds]);

    useEffect(() => {
        if (!hasActiveOtherSpellSelection) return;
        setOtherOpen(true);
    }, [hasActiveOtherSpellSelection]);

    const handleToggleWeapon = useCallback(() => {
        if (!build || !setBuild || isGenerating || !canEditGenerator) return;
        setBuild(build.toggleMaximizeWeapon());
    }, [build, setBuild, isGenerating, canEditGenerator]);

    const handleToggleBestWeapon = useCallback(() => {
        if (!build || !setBuild || isGenerating || !canEditGenerator) return;
        setBuild(build.toggleMaximizeBestWeapon());
    }, [build, setBuild, isGenerating, canEditGenerator]);

    const handleBestWeaponMetric = useCallback((metric) => {
        if (!build || !setBuild || isGenerating || !canEditGenerator) return;
        setBuild(build.setBestWeaponMetric(metric));
    }, [build, setBuild, isGenerating, canEditGenerator]);

    const prevWeaponCriteria = useRef(build?.maximizeWeapon === true);
    useEffect(() => {
        if (!canEditGenerator || !setGenConfig || !genConfig || !build) return;

        const on = build.maximizeWeapon === true;
        const wasOn = prevWeaponCriteria.current;
        prevWeaponCriteria.current = on;

        if (on) {
            const item = build.getWeapon();
            const desiredLock = item || true;
            const cur = genConfig.lockedSlots[WEAPON_SLOT];
            if (!weaponSlotLockMatches(cur, item)) {
                setGenConfig(
                    genConfig.update('lockedSlots', { ...genConfig.lockedSlots, [WEAPON_SLOT]: desiredLock })
                );
            }
        } else if (wasOn && !on) {
            const next = { ...genConfig.lockedSlots };
            delete next[WEAPON_SLOT];
            setGenConfig(genConfig.update('lockedSlots', next));
        }

        // Finding a weapon needs the slot free. A lock on an *empty* slot is
        // stored as `true` and pins it to nothing at all, so the search would
        // come back weaponless and the criterion would score zero — with
        // nothing on screen to explain why.
        if (build.maximizeBestWeapon === true && genConfig.lockedSlots[WEAPON_SLOT] === true) {
            const next = { ...genConfig.lockedSlots };
            delete next[WEAPON_SLOT];
            setGenConfig(genConfig.update('lockedSlots', next));
        }
    }, [build, genConfig, setGenConfig, canEditGenerator]);

    // The weapon-type filter here is the settings panel's filter, not a copy:
    // both toggle `genConfig.typeFilters`, so whichever the user reaches for,
    // the other shows it.
    const handleToggleWeaponType = useCallback((typeId) => {
        if (!canEditGenerator || !setGenConfig || !genConfig) return;
        setGenConfig(genConfig.toggleTypeFilter(typeId));
    }, [canEditGenerator, setGenConfig, genConfig]);


    // One card, three states of this panel — it used to be pasted into each.
    //
    // It renders with an empty weapon slot too, because the find-the-best-
    // weapon row is the one weapon criterion available in exactly that case.
    // On a page where nothing can be selected (community, compact) an empty
    // slot has nothing to say, so there it still needs a weapon.
    const showWeaponCard = !!weaponRow || canEditGenerator;
    const weaponCard = showWeaponCard ? (
        <WeaponSpellCard
            weapon={weaponRow}
            labels={labels}
            lang={lang}
            selected={weaponSelected}
            disabled={isGenerating}
            selectable={spellSelectable}
            onToggleWeapon={handleToggleWeapon}
            showWeight={showWeights}
            weight={weaponWeight}
            onWeightChange={handleWeaponWeightChange}
            mobileTargetCheckbox={spellMobileTargetCheckbox}
        />
    ) : null;

    // With the generator bar off, nothing on this panel asks the solver for
    // anything — so the two controls that do, find-me-a-weapon and the type
    // filter that narrows what it may pick, leave, and the row and the rule
    // that set them apart from the spells leave with them. What is left is the
    // equipped weapon, which is one card among the spells: it takes the first
    // cell of their grid.
    const weaponSection = canEditGenerator && weaponCard ? (
        <>
            <div className="spells-panel-weapons">
                {weaponCard}
                <BestWeaponCard
                    weapon={weaponRow}
                    lang={lang}
                    selected={bestWeaponSelected}
                    disabled={isGenerating}
                    selectable={spellSelectable}
                    onToggle={handleToggleBestWeapon}
                    metric={build?.getBestWeaponMetric?.()}
                    onMetricChange={handleBestWeaponMetric}
                    showWeight={showWeights}
                    weight={weaponWeight}
                    onWeightChange={handleWeaponWeightChange}
                />
                {genConfig ? (
                    <div className="spells-panel-weapon-filter">
                        <WeaponTypeFilter
                            typeFilters={genConfig.typeFilters}
                            onToggleType={handleToggleWeaponType}
                            lang={lang}
                            disabled={isGenerating}
                        />
                    </div>
                ) : null}
            </div>
            <hr className="spells-panel-divider" />
        </>
    ) : null;
    const inlineWeaponCard = canEditGenerator ? null : weaponCard;

    if (loading) {
        const loadingText = TXT_LOADING[lang] || TXT_LOADING.en;
        return (
            <div className="spells-panel-wrap" role="status" aria-busy="true" aria-live="polite">
                {weaponSection}
                <div className="spells-panel-loading">
                    <div className="spells-msg">{loadingText}</div>
                    {/* The skeletons carry their own aria-hidden, so the weapon
                        card can stand in the grid with them and still be read. */}
                    <div className="spells-panel-content spells-panel-content--loading">
                        {inlineWeaponCard}
                        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                            <SpellCardSkeleton key={`spell-skel-${i}`} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (err) {
        return (
            <div className="spells-panel-wrap">
                {weaponSection}
                {inlineWeaponCard ? (
                    <div className="spells-panel-content">{inlineWeaponCard}</div>
                ) : null}
                <div className="spells-panel-err-wrap">
                    <div className="spells-panel-err row">
                        <span className="spells-msg spells-panel-err-msg">{TXT_ERR[lang] || TXT_ERR.en}</span>
                        <button
                            type="button"
                            className="spells-panel-retry-button"
                            onClick={handleRetrySpells}
                            disabled={!typeId}
                        >
                            {TXT_RETRY[lang] || TXT_RETRY.en}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!showWeaponCard && regularRows.length === 0 && otherRows.length === 0 && summonRows.length === 0) {
        return (
            <div className="spells-panel-content">
                <div className="spells-msg">{TXT_EMPTY[lang] || TXT_EMPTY.en}</div>
            </div>
        );
    }

    return (
        <div className="spells-panel-wrap">
            {weaponSection}
            {regularRows.length > 0 || inlineWeaponCard ? (
                <div className="spells-panel-content">
                    {inlineWeaponCard}
                    {regularRows.map((sp) => (
                        <SpellCard
                            key={sp.id}
                            spell={sp}
                            labels={labels}
                            lang={lang}
                            eliotropePortalCardOnly={eliotropePortalCardOnly}
                            selected={canEditGenerator && selectedSpellSet.has(sp.id) && !unselectableSpellIds.has(sp.id)}
                            selectedLineKeys={visibleSelectedLineKeys}
                            disabled={isGenerating}
                            selectable={spellSelectable && !unselectableSpellIds.has(sp.id)}
                            onToggleSpellLine={handleToggleSpellLine}
                            spellCharges={spellCharges}
                            onChargesChange={handleChargesChange}
                            weights={weights}
                            mobileTargetCheckbox={spellMobileTargetCheckbox}
                        />
                    ))}
                </div>
            ) : null}
            {summonRows.length > 0 ? (
                <div className="spells-panel-section">
                    <SecTitle
                        title={TXT_SUMMONS[lang] || TXT_SUMMONS.en}
                        open={summonsOpen}
                        onToggle={() => setSummonsOpen((v) => !v)}
                    />
                    {summonsOpen ? (
                        <div className="spells-panel-content spells-panel-content--summons">
                            {summonRows.map((sp) => (
                                <SpellCard
                                    key={sp.id}
                                    spell={sp}
                                    labels={labels}
                                    lang={lang}
                                    eliotropePortalCardOnly={eliotropePortalCardOnly}
                                    selected={canEditGenerator && selectedSpellSet.has(sp.id) && !unselectableSpellIds.has(sp.id)}
                                    selectedLineKeys={visibleSelectedLineKeys}
                                    disabled={isGenerating}
                                    selectable={spellSelectable && !unselectableSpellIds.has(sp.id)}
                                    onToggleSpellLine={handleToggleSpellLine}
                                    spellCharges={spellCharges}
                                    onChargesChange={handleChargesChange}
                                    weights={weights}
                                    mobileTargetCheckbox={spellMobileTargetCheckbox}
                                />
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {otherRows.length > 0 ? (
                <div className="spells-panel-section">
                    <SecTitle
                        title={TXT_OTHER[lang] || TXT_OTHER.en}
                        open={otherOpen}
                        onToggle={() => setOtherOpen((v) => !v)}
                    />
                    {otherOpen ? (
                        <div className="spells-panel-content">
                            {otherRows.map((sp) => (
                                <SpellCard
                                    key={sp.id}
                                    spell={sp}
                                    labels={labels}
                                    lang={lang}
                                    eliotropePortalCardOnly={eliotropePortalCardOnly}
                                    selected={canEditGenerator && selectedSpellSet.has(sp.id) && !unselectableSpellIds.has(sp.id)}
                                    selectedLineKeys={visibleSelectedLineKeys}
                                    disabled={isGenerating}
                                    selectable={spellSelectable && !unselectableSpellIds.has(sp.id)}
                                    onToggleSpellLine={handleToggleSpellLine}
                                    spellCharges={spellCharges}
                                    onChargesChange={handleChargesChange}
                                    weights={weights}
                                    mobileTargetCheckbox={spellMobileTargetCheckbox}
                                />
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {showNote ? (
                <p className="spells-panel-note">
                    <Info className="spells-panel-note-icon" aria-hidden="true" />
                    {TXT_DAMAGE_WIP_NOTE[lang] || TXT_DAMAGE_WIP_NOTE.en}
                </p>
            ) : null}
        </div>
    );
};

export default SpellsPanel; 
